const MOOD_PLAY_ICON = '<svg class="moodplayer-control__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5v14l12-7z" /></svg>';
const MOOD_PAUSE_ICON = '<svg class="moodplayer-control__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /></svg>';
const MOOD_POLL_OPEN_MS = 900;
const MOOD_POLL_CLOSED_MS = 5000;
const MOOD_POLL_HUB_MS = 30000;
const MOOD_OPTIMISTIC_MS = 2500;

let moodBase: string | null = null;
let moodRoomKey = "";
let moodPollTimer = 0;
let moodIsPlaying = false;
let moodOptimisticUntil = 0;
let moodVolumeSeq = 0;
let moodVolumeStepPercent = 0;
let moodVolumeOptimisticUntil = 0;
let moodHubUp = false;
let moodRenderSeq = 0;

function InitMoodPlayer(base: string, roomKey: string): void {
    if (!base) {
        return;
    }
    moodBase = base.replace(/\/+$/, "");
    moodRoomKey = roomKey;
    MoodPlayerPoll();
    MoodPlayerConnect();
    MoodPlayerSchedule();
}

// The panel listens to the MoodPlayer's own hub, so a song change or somebody else's tap shows up as it
// happens. The poll below stays as the fallback and slows right down while the hub is up: a websocket
// across two origins is the first thing a network gets wrong, and a panel that quietly freezes is worse
// than one that is a few seconds behind.
function MoodPlayerConnect(): void {
    if (typeof signalR === "undefined" || !moodBase) {
        return;
    }

    // withCredentials off on purpose. The client sends cookies by default, and a cross-origin request that
    // carries credentials makes the browser demand an Access-Control-Allow-Credentials header on top of
    // everything else - for an endpoint that has no session to begin with, since the remote control is
    // public by room key. Turning it off means the panel needs nothing from the MoodPlayer that the
    // ordinary polling did not already need.
    const connection = new signalR.HubConnectionBuilder()
        .withUrl(moodBase + "/api/initiator/hub", { withCredentials: false })
        .withAutomaticReconnect()
        .build();

    connection.on("view", function (view: MoodView) {
        moodRenderSeq++;
        RenderMoodPlayer(view);
    });
    connection.onreconnecting(function () {
        moodHubUp = false;
        MoodPlayerSchedule();
    });
    connection.onreconnected(function () {
        MoodPlayerJoin(connection);
    });
    connection.onclose(function () {
        moodHubUp = false;
        MoodPlayerSchedule();
    });

    connection.start()
        .then(function () {
            MoodPlayerJoin(connection);
        })
        .catch(function () { });
}

function MoodPlayerJoin(connection: signalR.HubConnection): void {
    connection.invoke<MoodView>("JoinAsRemote", moodRoomKey)
        .then(function (view) {
            moodHubUp = true;
            moodRenderSeq++;
            RenderMoodPlayer(view);
            MoodPlayerSchedule();
        })
        .catch(function () { });
}

// A closed panel nobody is looking at does not need a fresh view every three seconds, and an open one
// wants a much fresher one than that. Same budget, spent where it shows. With the hub up the poll is not
// carrying the state anymore, so it drops back to a slow "are we still in touch" beat.
function MoodPlayerSchedule(): void {
    clearTimeout(moodPollTimer);

    const panel = TryById<HTMLElement>("moodplayerControl");
    const isOpen = panel !== null && panel.classList.contains("show");
    const delay = moodHubUp ? MOOD_POLL_HUB_MS : (isOpen ? MOOD_POLL_OPEN_MS : MOOD_POLL_CLOSED_MS);

    moodPollTimer = setTimeout(function () {
        MoodPlayerPoll();
        MoodPlayerSchedule();
    }, delay);
}

function ToggleMoodPlayer(): void {
    const panel = TryById<HTMLElement>("moodplayerControl");
    if (!panel) {
        return;
    }

    panel.classList.toggle("show");

    // Opening the panel should not show whatever the last five-second poll happened to catch.
    if (panel.classList.contains("show")) {
        MoodPlayerPoll();
    }

    MoodPlayerSchedule();
}

// A poll is a request in flight; a hub push or a join response is truth that already landed. Either can
// finish after this one started, which means this response is stale (it may even be a `null` from a
// transient fetch error) and applying it would flash the panel back to offline over data that is still
// current. The token records how fresh the world was when the request went out; if anything newer has
// rendered by the time it comes back, the response is dropped instead of applied.
function MoodPlayerPoll(): void {
    if (!moodBase) {
        return;
    }
    const token = ++moodRenderSeq;
    fetch(`${moodBase}/api/initiator/remote/view?roomKey=${encodeURIComponent(moodRoomKey)}`)
        .then(response => response.ok ? response.json() : null)
        .then(view => RenderIfCurrent(token, view))
        .catch(() => RenderIfCurrent(token, null));
}

function RenderIfCurrent(token: number, view: MoodView | null): void {
    if (token < moodRenderSeq) {
        return;
    }
    RenderMoodPlayer(view);
}

// Three quick taps are three requests, and they do not have to come back in the order they left. The
// answer to the first tap describes a quieter room than the third one you already made, so rendering it
// walked the number back down. Each tap gets a sequence number and anything older than the newest tap is
// dropped on arrival; in the meantime the panel counts along itself with the step the server reports.
function MoodVolume(direction: string): void {
    if (!moodBase) {
        return;
    }

    const seq = ++moodVolumeSeq;
    MoodPredictVolume(direction);

    fetch(`${moodBase}/api/initiator/remote/volume/${direction}?roomKey=${encodeURIComponent(moodRoomKey)}`, { method: "POST" })
        .then(response => response.ok ? response.json() : null)
        .then(view => {
            if (seq < moodVolumeSeq) {
                return;
            }
            moodVolumeOptimisticUntil = 0;
            RenderMoodPlayer(view);
            MoodRefreshSoon();
        })
        .catch(() => { });
}

function MoodPredictVolume(direction: string): void {
    const volumeElement = TryById<HTMLElement>("mpVolume");
    if (!volumeElement || !moodVolumeStepPercent) {
        return;
    }

    const shown = parseInt(volumeElement.textContent || "", 10);
    if (isNaN(shown)) {
        return;
    }

    const step = direction === "up" ? moodVolumeStepPercent : -moodVolumeStepPercent;
    volumeElement.textContent = Math.min(100, Math.max(0, shown + step)) + "%";
    moodVolumeOptimisticUntil = Date.now() + MOOD_OPTIMISTIC_MS;
}

function MoodSwitchPlaylist(playlistId: string): void {
    if (!moodBase) {
        return;
    }
    fetch(`${moodBase}/api/initiator/remote/playlist?roomKey=${encodeURIComponent(moodRoomKey)}&playlistId=${encodeURIComponent(playlistId)}`, { method: "POST" })
        .then(response => response.ok ? response.json() : null)
        .then(view => { RenderMoodPlayer(view); MoodRefreshSoon(); })
        .catch(() => { });
}

function MoodAction(action: string): void {
    if (!moodBase) {
        return;
    }
    fetch(`${moodBase}/api/initiator/remote/${action}?roomKey=${encodeURIComponent(moodRoomKey)}`, { method: "POST" })
        .then(response => response.ok ? response.json() : null)
        .then(view => { RenderMoodPlayer(view); MoodRefreshSoon(); })
        .catch(() => { });
}

// The player only picks a command up on its own poll, so the next few responses still describe the state
// from before the tap. Flip the icon straight away and ignore the server on this one field until the
// command has had time to land, otherwise the button visibly bounces back and then forward again.
function MoodPlayPause(): void {
    moodIsPlaying = !moodIsPlaying;
    moodOptimisticUntil = Date.now() + MOOD_OPTIMISTIC_MS;
    MoodRenderPlayPause();
    MoodAction("play-pause");
}

function MoodRenderPlayPause(): void {
    const playPauseElement = TryById<HTMLElement>("mpPlayPause");
    if (!playPauseElement) {
        return;
    }

    playPauseElement.innerHTML = moodIsPlaying ? MOOD_PAUSE_ICON : MOOD_PLAY_ICON;
}

function MoodNext(): void {
    MoodAction("next");
}

function MoodPrevious(): void {
    MoodAction("previous");
}

// The player applies remote commands on its own ~1.5s poll, so the fresh track title only lands a
// little later. Re-poll shortly after an action so the panel catches up without waiting for the
// regular 3s interval. (Volume, play/pause and active playlist already update optimistically.)
function MoodRefreshSoon(): void {
    setTimeout(MoodPlayerPoll, 600);
    setTimeout(MoodPlayerPoll, 1800);
}

function RenderMoodPlayer(view: MoodView | null): void {
    const statusElement = TryById<HTMLElement>("mpStatus");
    if (!statusElement) {
        return;
    }
    const nowElement = ById<HTMLElement>("mpNowPlaying");
    const volumeElement = ById<HTMLElement>("mpVolume");
    const listElement = ById<HTMLElement>("mpPlaylists");

    const online = view !== null && view.online;
    statusElement.textContent = online ? "online" : "offline";
    statusElement.className = "moodplayer-control__status " + (online ? "moodplayer-control__status--online" : "moodplayer-control__status--offline");

    if (Date.now() >= moodOptimisticUntil) {
        moodIsPlaying = online && view.isPlaying;
    }
    MoodRenderPlayPause();

    if (!online) {
        nowElement.innerHTML = "";
        volumeElement.textContent = "--%";
        listElement.innerHTML = "";
        return;
    }

    RenderMoodNowPlaying(nowElement, view);

    moodVolumeStepPercent = view.volumeStepPercent;
    if (Date.now() >= moodVolumeOptimisticUntil) {
        volumeElement.textContent = view.volumePercent + "%";
    }

    listElement.innerHTML = "";
    (view.openPlaylists || []).forEach(function (playlist) {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = playlist.name;
        button.className = "moodplayer-control__playlist" + (playlist.id === view.activePlaylistId ? " moodplayer-control__playlist--active" : "");
        button.onclick = function () {
            MoodSwitchPlaylist(playlist.id);
        };
        listElement.appendChild(button);
    });
}

// Stack title / artist / album, mirroring how a playlist card shows the current song.
function RenderMoodNowPlaying(nowElement: HTMLElement, view: MoodView): void {
    nowElement.innerHTML = "";
    const lines = [
        { text: view.currentSongTitle, className: "moodplayer-control__song" },
        { text: view.currentSongArtist, className: "moodplayer-control__meta" },
        { text: view.currentSongAlbum, className: "moodplayer-control__meta" },
    ];
    lines.forEach(function (line) {
        if (!line.text) {
            return;
        }
        const span = document.createElement("span");
        span.className = line.className;
        span.textContent = line.text;
        nowElement.appendChild(span);
    });
}
